import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import DraftEditor from "./DraftEditor";
import PaymentMethods from "./PaymentMethods";
import { fmtTime } from "./util";

const err = (e, m) => toast.error(formatApiError(e.response?.data?.detail) || m);
const field = "h-10 rounded border border-mist bg-white px-3 text-sm outline-none focus:border-sage-dark";

function ActiveCard({ ev, pending, onChanged }) {
    const close = async () => {
        const warn = pending ? `\n\n${pending} sale(s) on this device are not synced yet. They stay saved and will still sync after closing.` : "";
        if (!window.confirm(`Close "${ev.name}"? Sales will stop on the POS.${warn}`)) return;
        try {
            await api.post(`/pos/events/${ev.id}/close`);
            toast.success("Event closed");
            onChanged();
        } catch (e) { err(e, "Could not close event"); }
    };
    return (
        <div className="rounded-md border border-sage-dark/40 bg-white p-4 space-y-3" data-testid="pos-setup-active">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-sage-dark font-semibold">Active event · stock locked</p>
                    <p className="font-serif text-2xl text-ink">{ev.name}</p>
                    <p className="text-xs text-neutral-500">{ev.location_name} ({ev.location_code}) · started {fmtTime(ev.started_at)} · {ev.item_count} SKUs</p>
                </div>
                <button onClick={close} className="h-9 rounded border border-red-700 px-3 text-xs uppercase tracking-widest text-red-700 hover:bg-red-50" data-testid="pos-close-event">Close Event</button>
            </div>
            <PaymentMethods ev={ev} onSaved={onChanged} />
        </div>
    );
}

export default function Setup({ online, onChanged, pending }) {
    const [events, setEvents] = useState(null);
    const [locs, setLocs] = useState([]);
    const [name, setName] = useState("");
    const [loc, setLoc] = useState("");
    const [editing, setEditing] = useState(null);
    const load = useCallback(() => api.get("/pos/events").then(({ data }) => setEvents(data)).catch((e) => err(e, "Could not load events")), []);

    useEffect(() => {
        if (!online) return;
        load();
        api.get("/pos/warehouses").then(({ data }) => {
            setLocs(data);
            const vk = data.find((l) => /VINKOO/i.test(l.location_code || ""));
            if (vk) setLoc(String(vk.location_id));
        }).catch((e) => err(e, "Could not load Jubelio warehouses"));
    }, [online, load]);

    if (!online) return <p className="rounded bg-amber-50 p-4 text-sm text-amber-800" data-testid="pos-setup-offline">Setup needs an internet connection. Selling keeps working offline.</p>;
    if (editing) return <DraftEditor eventId={editing} onBack={() => { setEditing(null); load(); }} onStarted={async () => { setEditing(null); await load(); await onChanged(); }} />;

    const create = async () => {
        if (!name.trim() || !loc) return toast.error("Enter an event name and choose a warehouse");
        try {
            const { data } = await api.post("/pos/events", { name, location_id: Number(loc) });
            setName("");
            setEditing(data.id);
        } catch (e) { err(e, "Could not create event"); }
    };
    const remove = async (ev) => {
        if (!window.confirm(`Delete draft "${ev.name}"?`)) return;
        try { await api.delete(`/pos/events/${ev.id}`); load(); } catch (e) { err(e, "Could not delete draft"); }
    };
    const activeEv = events?.find((e) => e.status === "ACTIVE");
    const drafts = events?.filter((e) => e.status === "DRAFT") || [];
    const closed = events?.filter((e) => e.status === "CLOSED") || [];
    return (
        <div className="space-y-6" data-testid="pos-setup">
            {activeEv && <ActiveCard ev={activeEv} pending={pending} onChanged={async () => { await load(); await onChanged(); }} />}
            <section className="space-y-2">
                <h2 className="text-xs uppercase tracking-widest text-ink font-semibold">New Event</h2>
                <div className="flex flex-wrap gap-2">
                    <input className={`${field} flex-1 min-w-[200px]`} placeholder="Event name" value={name} onChange={(e) => setName(e.target.value)} data-testid="pos-new-event-name" />
                    <select className={`${field} min-w-[220px]`} value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="pos-new-event-warehouse">
                        <option value="">Jubelio warehouse…</option>
                        {locs.map((l) => <option key={l.location_id} value={l.location_id}>{l.location_name} / {l.location_code}</option>)}
                    </select>
                    <button onClick={create} className="h-10 rounded bg-sage-dark px-4 text-xs uppercase tracking-widest text-white" data-testid="pos-new-event-create">Create</button>
                </div>
            </section>
            <section className="space-y-2">
                <h2 className="text-xs uppercase tracking-widest text-ink font-semibold">Draft Events</h2>
                {!drafts.length && <p className="text-sm text-neutral-400">No drafts.</p>}
                {drafts.map((ev) => (
                    <div key={ev.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-mist bg-white px-3 py-2" data-testid={`pos-draft-${ev.id}`}>
                        <div><p className="text-sm text-ink">{ev.name}</p><p className="text-[11px] text-neutral-500">{ev.location_code} · {ev.item_count} SKUs</p></div>
                        <div className="flex gap-2">
                            <button onClick={() => setEditing(ev.id)} className="text-xs uppercase tracking-widest text-sage-dark" data-testid={`pos-draft-open-${ev.id}`}>Prepare Stock</button>
                            <button onClick={() => remove(ev)} className="text-xs uppercase tracking-widest text-red-700" data-testid={`pos-draft-delete-${ev.id}`}>Delete</button>
                        </div>
                    </div>
                ))}
            </section>
            {!!closed.length && (
                <section className="space-y-1">
                    <h2 className="text-xs uppercase tracking-widest text-ink font-semibold">Closed Events</h2>
                    {closed.map((ev) => <p key={ev.id} className="text-sm text-neutral-500">{ev.name} · {ev.location_code} · closed {fmtTime(ev.closed_at)}</p>)}
                </section>
            )}
        </div>
    );
}
